import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Row, Col, Card, CardHeader } from "reactstrap";
import { reportsApi } from "../../../../services/reportsServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-solid-svg-icons";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const InProgressManHoursChart = () => {
  const { id: jobId } = useParams();
  const [rawData, setRawData] = useState(null);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const result = await reportsApi.manHoursByLaborCode({ jobId });
        if (result && result.data) {
          setRawData(result.data);
        } else {
          setRawData(null);
        }
      } catch (error) {
        setRawData(null);
        console.error("Error fetching chart data:", error);
      }
    };
    fetchChartData();
  }, [jobId]);

  const { chartData } = useMemo(() => {
    if (!rawData || !rawData.laborCategories) {
      return {
        chartData: {
          labels: [],
          datasets: [],
        },
      };
    }

    const { laborCategories } = rawData;

    const labels = laborCategories.map((item) => `LC-${item.code}`);
    const estimatedHours = laborCategories.map((item) =>
      parseFloat(item.totalEstimatedHours)
    );
    const usedHours = laborCategories.map((item) =>
      parseFloat(item.totalManHours || 0)
    );

    return {
      chartData: {
        labels,
        datasets: [
          {
            label: "Estimated",
            data: estimatedHours,
            backgroundColor: "#2c3695",
            borderColor: "#2c3695",
            borderWidth: 1,
            borderRadius: 3,
            barPercentage: 0.45,
            categoryPercentage: 0.6,
          },
          {
            label: "Used",
            data: usedHours,
            backgroundColor: "#6a76eb",
            borderColor: "#6a76eb",
            borderWidth: 1,
            borderRadius: 3,
            barPercentage: 0.45,
            categoryPercentage: 0.6,
          },
        ],
      },
    };
  }, [rawData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          boxWidth: 30,
          boxHeight: 3,
          padding: 10,
        },
      },
      title: {
        display: false,
        text: "Man Hours by Labor Code",
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || "";
            const value = context.parsed.y || 0;
            return `${label}: ${value}`;
          },
        },
      },
      datalabels: {
        anchor: "end",
        align: "top",
        color: "#858585",
        font: {
          weight: "bold",
          size: 10,
        },
        formatter: (value) => value,
      },
    },
    scales: {
      x: {
        stacked: false,
        grid: {
          display: false,
        },
        barPercentage: 0.95,
        categoryPercentage: 0.95,
      },
      y: {
        stacked: false,
        beginAtZero: true,
        position: "left",
        display: true,
        border: {
          display: false,
        },
      },
    },
  };

  return (
    <Card className="p-3 m-0 h-100">
      <CardHeader className="bg-white border-0 p-0 mb-3">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <div className="rounded-circle d-flex align-items-center justify-content-center p-2 bg-soft-blue">
              <FontAwesomeIcon icon={faClock} size="lg" />
            </div>
            <h4 className="m-0 ml-3 font-weight-bold">Man hours</h4>
          </div>
        </div>
      </CardHeader>
      <Row>
        <Col xs="12" style={{ height: "300px" }}>
          <Bar data={chartData} options={chartOptions} plugins={[ChartDataLabels]} />
        </Col>
      </Row>
    </Card>
  );
};

export default InProgressManHoursChart;
